import { useEffect, useRef, useState } from "react";
import Image from "@Components/Image";
import Reveal from "@Components/Reveal/Reveal";
import mock from "../mock/mock.module.scss";
import styles from "./ListWithUsSteps.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import { pageAsset } from "@Utils/getStaticAssetPath";

// Images come from the static-assets bucket, never `import`ed — see `pageAsset`.
const asset = pageAsset("ListWithUsV2");
const triDown = asset("steps-tri-down.svg");
const triUp = asset("steps-tri-up.svg");

const thumbChapter = asset("step-thumb-1.jpg");
const thumbUrbanest = asset("step-thumb-2.jpg");
const thumbIq = asset("step-thumb-3.jpg");

/**
 * "Listing With amber In 3 Easy Steps" — Figma node 2456:6203.
 *
 * The rail plays itself. Each segment fills left to right, and the mock-up above
 * it plays its own beat while it does: the form types itself in under a caret,
 * the listings arrive, the booking figures count up. Steps waiting their turn
 * sit dimmed, so attention follows the rail.
 *
 * The rail walks the three steps once and then stays full — it is a progress
 * indicator, and one that empties and refills forever stops meaning anything.
 * The mock-ups go on cycling underneath, which is what keeps the section alive.
 * Only a reload sends the rail back to the start.
 */

const LISTINGS = [
  { thumb: thumbChapter, tag: "#4 RANKED IN LONDON", title: "Chapter Spitalfields" },
  { thumb: thumbUrbanest, tag: "#11 RANKED IN LONDON", title: "Urbanest Tower Bridge" },
  { thumb: thumbIq, tag: "#18 RANKED IN LONDON", title: "iQ Shoreditch" },
];

const BOOKINGS = [
  { name: "Patrick John", property: "Chapter Spitalfields", status: "IN PROGRESS", tone: "blue" },
  { name: "Sarah Mitchell", property: "Urbanest Tower Bridge", status: "INVOICED", tone: "green" },
];

/**
 * How many rail cells there are. The labels themselves are "Step 1"…"Step 3",
 * built from `list.v2.steps.raillabel` — a numbered label is one key with the
 * number interpolated, not three near-identical strings.
 */
const STEP_COUNT = 3;

/** How long one rail segment takes to fill. */
const SEGMENT_MS = 1400;
/** Beat between one segment finishing and the next starting. */
const HANDOVER_MS = 260;
/** How long the completed rail holds before the loop restarts. */
const HOLD_MS = 5000;
/**
 * One frame with the rail transition suppressed, so restarting snaps back to
 * zero instead of visibly draining right to left.
 */
const SNAP_MS = 40;
/** -1 is the resting state; 0-2 mean that step is currently filling. */
const AT_REST = -1;

/** Per-field typing duration and the beat between the two fields. */
const TYPE_MS = 560;
const GAP_MS = 140;

/** The booking figures count from a running total rather than from zero. */
const COUNT_MS = 1250;
const BOOKINGS_FROM = 3000;
const BOOKINGS_TO = 4849;
const INVOICED_FROM = 200;
const INVOICED_TO = 243;

/** Listing rows from this index on are the ones that arrive. */
const ARRIVES_FROM = 1;

const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

/** Counts from → to over COUNT_MS whenever `run` flips true. */
const useCountFrom = (from: number, to: number, run: boolean) => {
  const [value, setValue] = useState(to);

  useEffect(() => {
    if (!run) return undefined;

    let raf = 0;
    let start = 0;
    const step = (now: number) => {
      if (!start) start = now;
      const progress = Math.min(1, (now - start) / COUNT_MS);
      setValue(Math.round(from + (to - from) * easeOutCubic(progress)));
      if (progress < 1) raf = window.requestAnimationFrame(step);
    };

    setValue(from);
    raf = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(raf);
  }, [run, from, to]);

  return value;
};

/**
 * One field of the form, revealed a character at a time under a caret.
 *
 * The caret exists only while this field is being typed: it is not rendered at
 * rest, and the second delay in the pair hides it the moment the last character
 * lands, so nothing blinks once the writing is done.
 */
const TypedValue = ({
  text,
  playing,
  delay,
}: {
  text: string;
  playing: boolean;
  delay: number;
}) => (
  <span
    className={styles.typed}
    style={{ "--type-dur": `${TYPE_MS}ms`, "--type-steps": text.length } as React.CSSProperties}
  >
    <span
      className={playing ? styles.typedText : ""}
      style={playing ? { animationDelay: `${delay}ms` } : undefined}
    >
      {text}
    </span>
    {playing ? (
      <span
        className={`${styles.caret} ${styles.caretTyping}`}
        // Travel starts at `delay`; the hide fires one field-duration later.
        style={{ animationDelay: `${delay}ms, ${delay + TYPE_MS}ms` }}
        aria-hidden="true"
      />
    ) : null}
  </span>
);

const Steps = () => {
  const { t } = useTranslationHook();
  const sectionRef = useRef<HTMLElement>(null);
  const [active, setActive] = useState(AT_REST);
  const [snap, setSnap] = useState(false);
  /** Latched once the rail has filled all three segments; only a reload clears it. */
  const [railDone, setRailDone] = useState(false);
  const [inView, setInView] = useState(false);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return undefined;

    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setReduced(true);
      return undefined;
    }

    // Kept connected: the loop is endless, so it should stop costing anything
    // once the section is scrolled past.
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => setInView(entry.isIntersecting)),
      { threshold: 0.4 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const looping = inView && !reduced;

  useEffect(() => {
    if (!looping) return undefined;

    let timers: number[] = [];
    const at = (ms: number, fn: () => void) => timers.push(window.setTimeout(fn, ms));

    const runCycle = () => {
      timers.forEach(window.clearTimeout);
      timers = [];

      // Back to empty with transitions off, then start the first segment.
      setSnap(true);
      setActive(AT_REST);
      at(SNAP_MS, () => {
        setSnap(false);
        setActive(0);
      });

      let elapsed = SNAP_MS;
      for (let i = 1; i < STEP_COUNT; i += 1) {
        elapsed += SEGMENT_MS + HANDOVER_MS;
        at(elapsed, () => setActive(i));
      }

      // The last segment lands here. From this point the rail is finished for
      // good: it has said what it had to say, and watching it drain and refill
      // on a loop turns a progress indicator into wallpaper. The mock-ups keep
      // cycling underneath — that is what reads as live — but the rail holds
      // full until the page is reloaded.
      at(elapsed + SEGMENT_MS, () => setRailDone(true));

      elapsed += SEGMENT_MS + HOLD_MS;
      at(elapsed, runCycle);
    };

    runCycle();
    return () => timers.forEach(window.clearTimeout);
  }, [looping]);

  /** Step 1 reads as reached even at rest, which is what the frame draws. */
  const reached = active === AT_REST ? 0 : active;
  const isPlaying = (i: number) => active === i;
  const isPending = (i: number) => active !== AT_REST && i > active;

  const bookings = useCountFrom(BOOKINGS_FROM, BOOKINGS_TO, isPlaying(2));
  const invoiced = useCountFrom(INVOICED_FROM, INVOICED_TO, isPlaying(2));

  const cardClass = (i: number) => `${styles.card} ${isPending(i) ? styles.cardDimmed : ""}`;

  // One source for both progress rails — the horizontal one above the columns and the vertical
  // one beside each stacked step — so the two can never disagree about where the walk is.
  const segReached = (i: number) => railDone || i <= reached;
  const segStyle = (i: number) =>
    ({
      // Every rail starts empty and fills; nothing rests part-done. Once the walk is complete
      // every segment is pinned full, so the cycles that keep the mock-ups moving no longer
      // drag the rail back with them.
      "--p": railDone ? 1 : Number(active !== AT_REST && i <= active),
      "--d": snap && !railDone ? "0ms" : `${SEGMENT_MS}ms`,
    } as React.CSSProperties);

  return (
    <section ref={sectionRef} className={styles.section}>
      <div className={styles.inner}>
        <Reveal as="h2" className={styles.heading}>
          {t("list.v2.steps.heading")}
        </Reveal>

        <div className={styles.body}>
          <div className={styles.rail}>
            {Array.from({ length: STEP_COUNT }, (_, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div className={styles.railCell} key={i}>
                <span
                  className={`${styles.railLabel} ${segReached(i) ? styles.railLabelReached : ""}`}
                >
                  {t("list.v2.steps.raillabel", { number: String(i + 1) })}
                </span>
                <span className={styles.railTrack} aria-hidden="true">
                  <span className={styles.railFill} style={segStyle(i)} />
                </span>
              </div>
            ))}
          </div>

          <div className={styles.cols}>
            {/* Step 1 — the contact form, typing itself in. */}
            <div className={styles.col}>
              {/* The step's number, for the stacked layout — where `.rail` is hidden because
                three segments above one column say nothing about where you are in it. Hidden
                again wherever the rail shows, so a step is never numbered twice. */}
              <span
                className={`${styles.railLabel} ${segReached(0) ? styles.railLabelReached : ""} ${
                  styles.stepChip
                }`}
              >
                {t("list.v2.steps.raillabel", { number: "1" })}
              </span>
              {/* The stacked layout's progress rail: a node beside the number and a track
                running down the step, filling while this step's mock-up plays — the same
                state the horizontal rail shows, turned on its side. */}
              <span className={styles.vRail} aria-hidden="true">
                <span className={`${styles.vNode} ${segReached(0) ? styles.vNodeReached : ""}`} />
                <span className={styles.vTrack}>
                  <span className={styles.vFill} style={segStyle(0)} />
                </span>
              </span>
              <div className={styles.frame}>
                <div className={mock.bezel}>
                  <div className={cardClass(0)}>
                    <div className={mock.crumb}>
                      <span className={mock.crumbL}>
                        {t("list.v2.steps.mock.listyourproperty")}
                      </span>
                      <span className={mock.crumbR}>{t("list.v2.steps.mock.stepof")}</span>
                    </div>
                    <span className={`${mock.rule} ${styles.ruleA}`} />

                    <div className={styles.field}>
                      <span className={styles.fieldLabel}>
                        {t("list.v2.steps.mock.propertyname")}
                      </span>
                      <span className={styles.input}>
                        <TypedValue text="Chapter Spitalfields" playing={isPlaying(0)} delay={0} />
                      </span>
                    </div>
                    <div className={`${styles.field} ${styles.fieldSpaced}`}>
                      <span className={styles.fieldLabel}>{t("list.v2.steps.mock.city")}</span>
                      <span className={styles.input}>
                        <TypedValue text="London" playing={isPlaying(0)} delay={TYPE_MS + GAP_MS} />
                      </span>
                    </div>

                    <span className={styles.submit}>{t("list.v2.steps.mock.next")}</span>
                  </div>
                </div>
              </div>

              <div className={styles.copy}>
                <h3 className={styles.stepTitle}>{t("list.v2.steps.step1.title")}</h3>
                <p className={styles.stepBody}>{t("list.v2.steps.step1.body")}</p>
              </div>
            </div>

            {/* Step 2 — the listings arriving. */}
            <div className={styles.col}>
              {/* The step's number, for the stacked layout — where `.rail` is hidden because
                three segments above one column say nothing about where you are in it. Hidden
                again wherever the rail shows, so a step is never numbered twice. */}
              <span
                className={`${styles.railLabel} ${segReached(1) ? styles.railLabelReached : ""} ${
                  styles.stepChip
                }`}
              >
                {t("list.v2.steps.raillabel", { number: "2" })}
              </span>
              {/* The stacked layout's progress rail: a node beside the number and a track
                running down the step, filling while this step's mock-up plays — the same
                state the horizontal rail shows, turned on its side. */}
              <span className={styles.vRail} aria-hidden="true">
                <span className={`${styles.vNode} ${segReached(1) ? styles.vNodeReached : ""}`} />
                <span className={styles.vTrack}>
                  <span className={styles.vFill} style={segStyle(1)} />
                </span>
              </span>
              <div className={styles.frame}>
                <div className={mock.bezel}>
                  <div className={`${cardClass(1)} ${styles.cardFixed}`}>
                    <div className={mock.crumb}>
                      <span className={mock.crumbL}>{t("list.v2.steps.mock.listings")}</span>
                      <span className={mock.crumbR}>{t("list.v2.steps.mock.activecount")}</span>
                    </div>
                    <span className={`${mock.rule} ${styles.ruleB}`} />

                    <div className={`${mock.th} ${styles.thSpaced}`}>
                      <span>{t("list.v2.steps.mock.property")}</span>
                      <span>{t("list.v2.steps.mock.status")}</span>
                    </div>

                    {LISTINGS.map((listing, i) => {
                      const arrives = i >= ARRIVES_FROM && isPlaying(1);
                      return (
                        <div className={styles.listingGroup} key={listing.title}>
                          <span className={mock.rule} />
                          <div
                            className={`${styles.listingRow} ${arrives ? styles.arrives : ""}`}
                            style={
                              arrives
                                ? { animationDelay: `${(i - ARRIVES_FROM) * 220 + 260}ms` }
                                : undefined
                            }
                          >
                            <div className={styles.listingLeft}>
                              <Image
                                src={listing.thumb}
                                alt=""
                                className={mock.thumb}
                                width={32}
                                height={32}
                                isNotLazy
                              />
                              <div className={mock.entry}>
                                <span className={mock.entryTag}>{listing.tag}</span>
                                <span className={mock.entryTitle}>{listing.title}</span>
                              </div>
                            </div>
                            <span className={`${mock.badge} ${mock.badgeGreen}`}>
                              {t("list.v2.steps.mock.active")}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className={styles.copy}>
                <h3 className={styles.stepTitle}>{t("list.v2.steps.step2.title")}</h3>
                <p className={styles.stepBody}>{t("list.v2.steps.step2.body")}</p>
              </div>
            </div>

            {/* Step 3 — the bookings counting up. */}
            <div className={styles.col}>
              {/* The step's number, for the stacked layout — where `.rail` is hidden because
                three segments above one column say nothing about where you are in it. Hidden
                again wherever the rail shows, so a step is never numbered twice. */}
              <span
                className={`${styles.railLabel} ${segReached(2) ? styles.railLabelReached : ""} ${
                  styles.stepChip
                }`}
              >
                {t("list.v2.steps.raillabel", { number: "3" })}
              </span>
              {/* The stacked layout's progress rail: a node beside the number and a track
                running down the step, filling while this step's mock-up plays — the same
                state the horizontal rail shows, turned on its side. */}
              <span className={styles.vRail} aria-hidden="true">
                <span className={`${styles.vNode} ${segReached(2) ? styles.vNodeReached : ""}`} />
                <span className={styles.vTrack}>
                  <span className={styles.vFill} style={segStyle(2)} />
                </span>
              </span>
              <div className={styles.frame}>
                <div className={mock.bezel}>
                  <div className={`${cardClass(2)} ${styles.cardFixed}`}>
                    <div className={mock.crumb}>
                      <span className={mock.crumbL}>{t("list.v2.steps.mock.bookings")}</span>
                      <span className={mock.crumbR}>{t("list.v2.steps.mock.thismonth")}</span>
                    </div>
                    <span className={`${mock.rule} ${styles.ruleA}`} />

                    <div className={styles.stats}>
                      <div className={mock.stat}>
                        <span className={mock.label}>{t("list.v2.steps.mock.totalbookings")}</span>
                        <span className={mock.statValue}>{bookings.toLocaleString("en-US")}</span>
                        <span className={mock.delta}>
                          <Image
                            src={triDown}
                            alt=""
                            width={24}
                            height={24}
                            className={mock.deltaIcon}
                            isNotLazy
                          />
                          <span className={`${mock.deltaValue} ${mock.deltaDown}`}>3%</span>
                          <span className={mock.deltaNote}>
                            {t("list.v2.steps.mock.vslastweek")}
                          </span>
                        </span>
                        <span className={`${mock.bar} ${mock.barViolet} ${styles.fullBar}`} />
                      </div>
                      <div className={mock.stat}>
                        <span className={mock.label}>{t("list.v2.steps.mock.invoiced")}</span>
                        <span className={mock.statValue}>{invoiced.toLocaleString("en-US")}</span>
                        <span className={mock.delta}>
                          <Image
                            src={triUp}
                            alt=""
                            width={24}
                            height={24}
                            className={mock.deltaIcon}
                            isNotLazy
                          />
                          <span className={`${mock.deltaValue} ${mock.deltaUp}`}>1%</span>
                          <span className={mock.deltaNote}>
                            {t("list.v2.steps.mock.vslastweek")}
                          </span>
                        </span>
                        <span className={`${mock.bar} ${mock.barTeal} ${styles.fullBar}`} />
                      </div>
                    </div>

                    <span className={`${mock.rule} ${styles.ruleC}`} />

                    {BOOKINGS.map((booking, i) => (
                      <div className={styles.bookingGroup} key={booking.name}>
                        {i > 0 ? <span className={mock.rule} /> : null}
                        <div
                          className={`${styles.bookingRow} ${isPlaying(2) ? styles.arrives : ""}`}
                          style={
                            isPlaying(2) ? { animationDelay: `${520 + i * 220}ms` } : undefined
                          }
                        >
                          <div className={mock.entry}>
                            <span className={mock.entryTitle}>{booking.name}</span>
                            <span className={mock.entryNote}>{booking.property}</span>
                          </div>
                          <span
                            className={`${mock.badge} ${
                              booking.tone === "blue" ? mock.badgeBlue : mock.badgeGreen
                            }`}
                          >
                            {booking.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className={styles.copy}>
                <h3 className={styles.stepTitle}>{t("list.v2.steps.step3.title")}</h3>
                <p className={styles.stepBody}>{t("list.v2.steps.step3.body")}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default wrapperHOC(Steps, {
  componentName: "Steps-ListWithUs",
  showForChina: true,
});
